// Точка отправки событий без зависимостей: модули данных (покупка) шлют сюда, не зная про gtag, Telegram и окно.
// Приёмник ставит analytics.ts при запуске. До того события копятся в очереди и уходят при установке приёмника — так
// не теряется первый экран холодного старта (маршрутизатор создаётся при импорте App, раньше initAnalytics). Аналитика
// выключена (dev, тесты без приёмника) — setSink(null): очередь сбрасывается, дальше события молча пропадают.
import { actionName } from './events';
import type { EventName } from './catalog';

export type Params = Record<string, string | number | boolean | undefined>;
/** done — приёмник сообщает, что событие фактически ушло в сеть; передаётся только из trackBeforeUnload. */
type Sink = (name: EventName, params: Params, done?: () => void) => void;
type Event = [name: EventName, params: Params];
/** Предел очереди до запуска: защита от утечки, если приёмник так и не поставят. */
const QUEUE_LIMIT = 100;
let sink: Sink | null = null;
let queue: Event[] | null = [];

/** Ставит приёмник: сначала уходят lead (tg_launch — первым событием сессии), потом не ушедшие до прошлой перезагрузки
 *  (outbox, trackBeforeUnload), потом накопленное до запуска. null — аналитики не будет: очередь и outbox сбрасываются. */
export function setSink(fn: Sink | null, lead: Event[] = []): void {
  sink = fn;
  const pending = queue ?? [];
  queue = null;
  const carried = takeOutbox(Date.now());
  if (fn) for (const [name, params] of [...lead, ...carried, ...pending]) send(name, params);
}
function send(name: EventName, params: Params): void {
  try { sink?.(name, params); } catch { /* аналитика не роняет приложение */ }
}
export function track(name: EventName, params: Params = {}): void {
  if (queue) { if (queue.length < QUEUE_LIMIT) queue.push([name, params]); return; }
  send(name, params);
}

/** Предел ожидания отправки перед уходом со страницы: дольше человек не ждёт перезапуска после выбора языка. */
export const UNLOAD_LIMIT_MS = 1000;

/** Событие перед уходом со страницы (смена языка → перезагрузка). Обещание исполняется, когда приёмник сообщил, что
 *  событие фактически ушло в сеть (analytics.ts: запрос g/collect после события), или по пределу limitMs. gtag копит
 *  события пачкой и шлёт её через секунды — перезагрузка раньше теряла событие (HQ-607). Не дождались отправки (пачка
 *  ещё не ушла, gtag не загружен) или приёмника ещё нет — событие ложится в outbox и уходит первым после перезапуска,
 *  с экраном, на котором случилось. Не зависает и не бросает. */
export function trackBeforeUnload(name: EventName, params: Params, limitMs = UNLOAD_LIMIT_MS): Promise<void> {
  const carried: Params = { ...params, screen: screen || undefined };
  return new Promise((resolve) => {
    if (queue) { keepForNextStart(name, carried); resolve(); return; }
    if (!sink) { resolve(); return; } // аналитика выключена
    let settled = false;
    const finish = (sent: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (!sent) keepForNextStart(name, carried);
      resolve();
    };
    const timer = setTimeout(() => finish(false), limitMs);
    try { sink(name, params, () => finish(true)); } catch { finish(false); }
  });
}

// ---- Outbox: события перед уходом, отправку которых не дождались. Хранится до следующего запуска в localStorage;
// старше суток — не досылаем (это уже не «то же посещение»), больше OUTBOX_LIMIT — не копим.
const OUTBOX_KEY = 'cricket.analytics_outbox';
const OUTBOX_LIMIT = 20;
const OUTBOX_TTL_MS = 24 * 3600 * 1000;
type Carried = { name: EventName; params: Params; at: number };
function readOutbox(): Carried[] {
  try {
    const list: unknown = JSON.parse(localStorage.getItem(OUTBOX_KEY) ?? '[]');
    return Array.isArray(list) ? list.filter((e: Partial<Carried> | null): e is Carried =>
      typeof e?.name === 'string' && typeof e.at === 'number' && typeof e.params === 'object' && e.params !== null) : [];
  } catch { return []; }
}
function keepForNextStart(name: EventName, params: Params): void {
  try {
    const list = [...readOutbox(), { name, params, at: Date.now() }].slice(-OUTBOX_LIMIT);
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(list));
  } catch { /* приватный режим — событие потеряется, как и раньше */ }
}
function takeOutbox(now: number): Event[] {
  const list = readOutbox();
  try { localStorage.removeItem(OUTBOX_KEY); } catch { /* приватный режим */ }
  return list.filter((e) => now - e.at < OUTBOX_TTL_MS).map((e) => [e.name, e.params]);
}

// ---- Экран, на котором человек сейчас (имя словаря v1, ставит analytics.trackScreen): source действий — звезда
// избранного, «Поделиться», ролик. До первого экрана — unattributed.
let screen = '';
export function noteScreen(name: string): void { screen = name; }
export const currentScreen = (): string => screen || 'unattributed';

// ---- Свойства пользователя (favorite_teams_count): шлём только изменение; до запуска аналитики — помним последнее.
type PropsSink = (props: Record<string, string>) => void;
let propsSink: PropsSink | null = null;
const props = new Map<string, string>();
const sentProps = new Map<string, string>();
function flushProps(): void {
  if (!propsSink) return;
  const changed: Record<string, string> = {};
  for (const [k, v] of props) if (sentProps.get(k) !== v) { changed[k] = v; sentProps.set(k, v); }
  if (Object.keys(changed).length) try { propsSink(changed); } catch { /* аналитика не роняет приложение */ }
}
export function setPropsSink(fn: PropsSink | null): void { propsSink = fn; flushProps(); }
export function setUserProperty(name: string, value: string): void { props.set(name, value); flushProps(); }

// ---- Обезличенный номер пользователя (analytics_id бэкенда: HMAC внутреннего id, не Telegram id) — склейка событий
// Google с данными сервера и одного человека на разных устройствах. Приходит в ответах входа; помним между запусками.
const USER_KEY = 'cricket.analytics_id';
let userSink: ((id: string) => void) | null = null;
export function setUserSink(fn: typeof userSink): void { userSink = fn; }
export function savedAnalyticsUser(): string | null {
  try { return localStorage.getItem(USER_KEY); } catch { return null; }
}
export function rememberAnalyticsUser(id: unknown): void {
  if (typeof id !== 'string' || !/^[A-Za-z0-9_-]{8,64}$/.test(id)) return;
  try { localStorage.setItem(USER_KEY, id); } catch { /* приватный режим */ }
  try { userSink?.(id); } catch { /* аналитика не роняет вход */ }
}

/** Покупка за Stars: какой товар (по адресу счёта), чем кончилось — paid | cancelled | failed | pending — и цена в Stars
 *  с кнопки (целое; нет цены в Stars — без параметра). */
export function trackInvoice(endpoint: string, status: string, stars?: number): void {
  const price = Number.isInteger(stars) && stars! > 0 ? stars : undefined;
  track('stars_invoice', { item: actionName('POST', endpoint)?.replace(/^a_/, '') ?? 'unknown', status, stars: price });
}

/** Интерес к товару: view — строка товара попала на экран, click — нажали (state: ok — можно купить, blocked — кнопка
 *  недоступна: не хватает валюты, закрыто). Нажатие по недоступной кнопке — это и есть «захотел, но не смог». */
export function trackItem(kind: 'view' | 'click', item: string, extra: Params = {}): void {
  track(kind === 'view' ? 'item_view' : 'item_click', { item: item.slice(0, 60), ...extra });
}

/** Окно поверх сцены мира (не маршрут): карточка игрока, сезон, награды. */
export function trackSheet(name: string): void {
  track('sheet_view', { sheet: name.slice(0, 60) });
}
